/**
 * A replicated growable array (RGA), the sequence CRDT family Converge
 * is built on, cut down to run in the page.
 *
 * Every character gets a Lamport id [counter, site]. An insert names the
 * id it goes after; a delete tombstones an id and never removes it.
 * Integration rule: start right after the parent and skip every element
 * whose id is greater than the new one. Concurrent inserts at the same
 * spot therefore land in the same order on every replica, whatever
 * order the ops arrive in.
 *
 * Delivery must be causal (an op's parent arrives before the op). With
 * two replicas and one FIFO queue per direction, that holds for free.
 */

export const compareId = (a, b) => {
  if (a[0] !== b[0]) return a[0] - b[0];
  if (a[1] === b[1]) return 0;
  return a[1] < b[1] ? -1 : 1;
};

export const formatId = (id) => (id ? `${id[0]}@${id[1]}` : 'head');

export class Replica {
  constructor(site) {
    this.site = site;
    this.clock = 0;
    this.items = [];
    this.byId = new Map();
  }

  visible() {
    return this.items.filter((item) => !item.deleted);
  }

  text() {
    return this.visible()
      .map((item) => item.value)
      .join('');
  }

  tombstones() {
    return this.items.length - this.visible().length;
  }

  /** Integrate one op, local or remote. Idempotent: replays are ignored. */
  apply(op) {
    this.clock = Math.max(this.clock, op.id[0]);

    if (op.type === 'ins') {
      const key = formatId(op.id);
      if (this.byId.has(key)) return false;

      let pos = 0;
      if (op.after) {
        const parent = this.byId.get(formatId(op.after));
        pos = this.items.indexOf(parent) + 1;
      }
      while (pos < this.items.length && compareId(this.items[pos].id, op.id) > 0) {
        pos += 1;
      }

      const item = { id: op.id, value: op.value, deleted: false };
      this.items.splice(pos, 0, item);
      this.byId.set(key, item);
      return true;
    }

    const target = this.byId.get(formatId(op.target));
    if (!target || target.deleted) return false;
    target.deleted = true;
    return true;
  }

  /**
   * Turn a whole-string edit from a textarea into ops. One contiguous
   * change per keystroke (or paste), found by trimming the common
   * prefix and suffix.
   */
  edit(nextText) {
    const vis = this.visible();
    const prev = vis.map((item) => item.value);
    const next = Array.from(nextText);

    let p = 0;
    while (p < prev.length && p < next.length && prev[p] === next[p]) p += 1;

    let s = 0;
    while (
      s < prev.length - p &&
      s < next.length - p &&
      prev[prev.length - 1 - s] === next[next.length - 1 - s]
    ) {
      s += 1;
    }

    const ops = [];

    for (let i = p; i < prev.length - s; i += 1) {
      this.clock += 1;
      const op = { type: 'del', id: [this.clock, this.site], target: vis[i].id };
      this.apply(op);
      ops.push(op);
    }

    let after = p > 0 ? vis[p - 1].id : null;
    for (const value of next.slice(p, next.length - s)) {
      this.clock += 1;
      const op = { type: 'ins', id: [this.clock, this.site], after, value };
      this.apply(op);
      ops.push(op);
      after = op.id;
    }

    return ops;
  }
}
